import type {
  AccountInventoryError,
  AccountInventoryRow,
  GetAccountsPayload,
} from '../accounts.model';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function coerceBalance(value: unknown): number | null | undefined {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return undefined;
}

function optionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function optionalBoolean(record: Record<string, unknown>, key: string): boolean | undefined {
  const value = record[key];
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeAccountRow(value: unknown): AccountInventoryRow {
  if (!isRecord(value)) {
    return {};
  }
  return {
    id: optionalString(value, 'id'),
    idMasked: optionalString(value, 'idMasked'),
    name: optionalString(value, 'name'),
    institution: optionalString(value, 'institution'),
    type: optionalString(value, 'type'),
    status: optionalString(value, 'status'),
    balance: coerceBalance(value['balance']),
    isOpen: optionalBoolean(value, 'isOpen'),
    isActive: optionalBoolean(value, 'isActive'),
  };
}

function isAccountError(value: unknown): value is AccountInventoryError {
  return isRecord(value) && typeof value['type'] === 'string' && typeof value['message'] === 'string';
}

/**
 * Envelope only: `accounts` is an array. Individual rows are not required to pass
 * — a single malformed account must not fail the whole GET.
 */
export function isGetAccountsPayload(value: unknown): value is GetAccountsPayload {
  if (!isRecord(value)) {
    return false;
  }
  if (!Array.isArray(value['accounts'])) {
    return false;
  }
  if (value['errors'] === undefined) {
    return true;
  }
  return Array.isArray(value['errors']) && value['errors'].every(isAccountError);
}

/** Preserve every returned row and normalize malformed fields to missing values. */
export function normalizeAccountsPayload(payload: GetAccountsPayload): GetAccountsPayload {
  return {
    accounts: (payload.accounts as unknown[]).map(normalizeAccountRow),
    errors: payload.errors,
  };
}
